import React, { useEffect, useState, useRef } from 'react';
import { Row, Col, Card, Button, Form } from 'react-bootstrap';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from '../../common/Toast/toast';
import PageTitle from '../../common/PageTitle.jsx';
import '../../common/SettingsStepForm.css';
import {
  createAssignModule,
  getAssignModuleById,
  updateAssignModule,
} from '../../../../services/AssignModulesService.js';

// Validation schema
const validationSchema = Yup.object().shape({
  label: Yup.string().trim().required('Label is required'),
  value: Yup.string().trim().required('Value is required'),
  url: Yup.string().trim().required('URL is required'),
});

/* The rules that turn a label into the two things derived from it.
   Named because several places have to agree: the label's onChange, the
   "use the generated one" links, and the checks for whether each is
   still the derived value. */
const slugify = (label) =>
  String(label || '').toLowerCase().trim().replace(/\s+/g, '-');

const urlify = (label) => {
  const slug = slugify(label);
  return slug ? '/' + slug : '';
};

const WarnIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v5M12 16.5v.01" />
  </svg>
);

/* The palette's own tokens rather than a second theme of this
   screen's own. These were literal oranges, so this popup stayed
   orange whichever palette was chosen while the app around it moved.
   ThemeContext writes the --pg-* custom properties onto the document,
   and a custom property works in an inline style exactly as it does in
   a stylesheet, so naming them here is the whole of the fix.

   Ink is deliberately not a palette value (see css/theme/palettes.css):
   text and its muted partner hold their contrast under every palette,
   so they stay literal. */
const THEME = {
  primary: 'var(--pg-accent)',
  // A chip that carries white text: the accent proper is the value the
  // palette engine guarantees white against -- a lighter tint is not.
  primaryLight: 'var(--pg-accent)',
  // Accent as TEXT wants the deeper one, the same rule ChatLayout follows.
  primaryDark: 'var(--pg-accent-hover)',
  textOnPrimary: '#FFFFFF',
  secondary: 'var(--pg-accent)',
  accent: 'var(--pg-accent-hover)',
  background: 'var(--pg-accent-soft)',
  cardBg: 'var(--pg-card)',
  border: 'var(--pg-line)',
  text: '#333333',
  textLight: '#666666',
};

// Filter Section Component
const FilterSection = ({ title, children }) => {
  return (
    <div
      className="filter-section mb-4 p-4 bg-dark-2"
      style={{
        borderRadius: '15px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        // background: THEME.cardBg,
        border: `1px solid ${THEME.border}`,
      }}
    >
      <h5
        className="mb-3"
        style={{
          borderBottom: `2px solid ${THEME.primary}`,
          paddingBottom: '8px',
          color: THEME.primaryDark,
        }}
      >
        {title}
      </h5>
      {children}
    </div>
  );
};

function AddAssignModules({
  // Shown inside a dialog rather than as a page: the component drops its
  // own back-link and toast host, since the dialog and the list supply
  // them. The card chrome around it is flattened by SettingsFormModal.css.
  embedded = false,
  // The record to edit. Falls back to the :id route param.
  recordId,
  // Called after a successful save instead of navigating away.
  onDone,
  // Called when the form is dismissed instead of navigating away.
  onCancel,
}) {
  const navigate = useNavigate();

  // Leaving the form means "go back to the list" on the route, and "close
  // the dialog" when embedded. Every exit goes through these two so the
  // component never has to know which it is.
  const leave = () => {
    if (onCancel) onCancel();
    else navigate('/setting/list-modules');
  };

  const finish = () => {
    if (onDone) onDone();
    else navigate('/setting/list-modules');
  };
  const { id: routeId } = useParams();
  const id = recordId ?? routeId;
  const [moduleData, setModuleData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const formikBagRef = useRef(null);

  // Initial form values
  const initialValues = {
    label: '',
    value: '',
    url: '',
  };

  // Fetch module details for editing
  const fetchModuleDetails = async (moduleId) => {
    try {
      const response = await getAssignModuleById(moduleId);
      const moduleData = response.data;

      if (moduleData) {
        setModuleData(moduleData);
        setIsEditing(true);

        // Set initial values after we have the data
        if (formikBagRef.current) {
          formikBagRef.current.setValues({
            label: moduleData.label || '',
            value: moduleData.value || '',
            url: moduleData.url || '',
          });
        }

        return moduleData;
      } else {
        setModuleData(null);
        setIsEditing(false);
        return null;
      }
    } catch (error) {
      console.error('Error fetching module details:', error);
      toast.error('Failed to fetch module details');
      return null;
    }
  };

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);

      try {
        // Then fetch module details if editing
        if (id) {
          await fetchModuleDetails(id);
        } else {
          setModuleData(null);
          setIsEditing(false);
        }
      } catch (error) {
        console.error('Error initializing data:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, [id]);

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Prepare payload according to your requirements
      const payload = {
        label: values.label,
        value: values.value,
        url: values.url,
      };

      if (isEditing) {
        await updateAssignModule(id, payload);
        toast.success('Module assignment updated successfully!');
      } else {
        await createAssignModule(payload);
        toast.success('Module assignment added successfully!');
      }

      resetForm();
      setModuleData(null);
      setIsEditing(false);
      finish();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || 'Error processing module!';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading spinner while data is being fetched
  if (loading) {
    // The page's spinner reserves 200px inside its own padding. The
    // dialog gets the bare line the other settings dialogs use.
    if (embedded) {
      return (
        <div className="sf">
          <div className="sf-empty">Loading module…</div>
        </div>
      );
    }

    return (
      <div style={{ padding: '20px' }}>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '200px' }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  /* ── The dialog build ───────────────────────────────────────────────
     One step: three fields, and two of them are generated from the
     first. Splitting that across screens would be ceremony. The field
     language is the `sf-*` one the stepped settings dialogs use, so this
     dialog and those read as the same family.
     ------------------------------------------------------------------ */
  if (embedded) {
    return (
      <div className="sf">
        <Formik
          initialValues={
            moduleData
              ? {
                  label: moduleData.label || '',
                  value: moduleData.value || '',
                  url: moduleData.url || '',
                }
              : initialValues
          }
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, setFieldValue, setFieldTouched, submitForm, isSubmitting }) => {
            const bad = (n) => touched[n] && errors[n];

            // Each of the two still matches what the label would generate,
            // so each can go on tracking it. Once one does not, the label
            // stops overwriting that one -- a typed value is the reader's,
            // not ours -- while the other keeps following.
            const derivedValue = slugify(values.label);
            const derivedUrl = urlify(values.label);
            const valueIsAuto = values.value === derivedValue;
            const urlIsAuto = values.url === derivedUrl;

            const save = () => {
              ['label', 'value', 'url'].forEach((n) => setFieldTouched(n, true, true));
              submitForm();
            };

            return (
              <Form
                noValidate
                onSubmit={(e) => {
                  // Nothing in here is type="submit", so the only thing
                  // that reaches this is Enter pressed inside a field.
                  e.preventDefault();
                  save();
                }}
              >
                <div className="sf-field">
                  <div className="sf-label-row">
                    <label className="sf-label" htmlFor="am-label">
                      Label<span className="sf-req">*</span>
                    </label>
                  </div>
                  <input
                    id="am-label"
                    type="text"
                    className={'sf-input' + (bad('label') ? ' sf-input--bad' : '')}
                    value={values.label}
                    placeholder="Roles, Batches, Attendance…"
                    autoComplete="off"
                    onChange={(e) => {
                      const next = e.target.value;
                      setFieldValue('label', next);
                      // Each follows only while it is still the generated one.
                      if (valueIsAuto) setFieldValue('value', slugify(next));
                      if (urlIsAuto) setFieldValue('url', urlify(next));
                    }}
                    onBlur={() => setFieldTouched('label', true, true)}
                  />
                  {bad('label') ? (
                    <p className="sf-error"><WarnIcon />{errors.label}</p>
                  ) : (
                    <p className="sf-hint">
                      How this module reads wherever permissions are listed.
                      The key and the path below follow from it.
                    </p>
                  )}
                </div>

                {/* Both are generated from the label, so both hang off it. */}
                <div className="sf-derived">
                  <div className="sf-row">
                    <div className="sf-field">
                      <div className="sf-label-row">
                        <label className="sf-label" htmlFor="am-value">
                          Value<span className="sf-req">*</span>
                          {valueIsAuto ? (
                            <span className="sf-state sf-state--auto">From label</span>
                          ) : (
                            <>
                              <span className="sf-state sf-state--custom">Edited</span>
                              {derivedValue && (
                                <button
                                  type="button"
                                  className="sf-relink"
                                  onClick={() => setFieldValue('value', derivedValue)}
                                >
                                  use {derivedValue}
                                </button>
                              )}
                            </>
                          )}
                        </label>
                      </div>
                      <input
                        id="am-value"
                        type="text"
                        className={'sf-input sf-input--slug' + (bad('value') ? ' sf-input--bad' : '')}
                        value={values.value}
                        placeholder="roles"
                        autoComplete="off"
                        spellCheck="false"
                        onChange={(e) =>
                          setFieldValue('value', e.target.value.replace(/\s+/g, '-'))
                        }
                        onBlur={() => setFieldTouched('value', true, true)}
                      />
                      {bad('value') ? (
                        <p className="sf-error"><WarnIcon />{errors.value}</p>
                      ) : (
                        <p className="sf-hint">The key permission checks use.</p>
                      )}
                    </div>

                    <div className="sf-field">
                      <div className="sf-label-row">
                        <label className="sf-label" htmlFor="am-url">
                          URL<span className="sf-req">*</span>
                          {urlIsAuto ? (
                            <span className="sf-state sf-state--auto">From label</span>
                          ) : (
                            <>
                              <span className="sf-state sf-state--custom">Edited</span>
                              {derivedUrl && (
                                <button
                                  type="button"
                                  className="sf-relink"
                                  onClick={() => setFieldValue('url', derivedUrl)}
                                >
                                  use {derivedUrl}
                                </button>
                              )}
                            </>
                          )}
                        </label>
                      </div>
                      <input
                        id="am-url"
                        type="text"
                        className={'sf-input sf-input--slug' + (bad('url') ? ' sf-input--bad' : '')}
                        value={values.url}
                        placeholder="/roles"
                        autoComplete="off"
                        spellCheck="false"
                        onChange={(e) => setFieldValue('url', e.target.value)}
                        onBlur={() => setFieldTouched('url', true, true)}
                      />
                      {bad('url') ? (
                        <p className="sf-error"><WarnIcon />{errors.url}</p>
                      ) : (
                        <p className="sf-hint">The route this module opens.</p>
                      )}
                    </div>
                  </div>
                </div>

                <footer className="sf-foot">
                  <button key="cancel" type="button" className="sf-btn" onClick={leave}>
                    Cancel
                  </button>

                  <span className="sf-foot-gap" />

                  {/* Not type="submit": a submit button inherits the
                      browser's default action for a click, which is how a
                      button that changes identity mid-click can save a form
                      it was not meant to. Nothing here is one. */}
                  <button
                    key="save"
                    type="button"
                    className="sf-btn sf-btn--main"
                    disabled={isSubmitting}
                    onClick={save}
                  >
                    {isSubmitting && <span className="sf-spin" aria-hidden="true" />}
                    {isSubmitting
                      ? isEditing ? 'Updating…' : 'Saving…'
                      : isEditing ? 'Update module' : 'Create module'}
                  </button>
                </footer>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  }

  return (
    <>
      {/* Page-only: the list behind the dialog already has a toast host,
          and two would show every message twice. */}
      <div style={{ padding: '20px' }}>
        {/* The page's way back. The dialog has its own close button and a
            Cancel, and this one would navigate out from under it. */}
        {!embedded && (
          <Link to="/setting/list-modules">
            <Button
              style={{
                backgroundColor: THEME.primary,
                borderColor: THEME.primary,
                borderRadius: '30px',
                padding: '8px 20px',
              }}
              className="mb-5"
            >
              Go to Module List
            </Button>
          </Link>
        )}
        <PageTitle pageTitle={isEditing ? 'Edit Module Assignment' : 'Add Module Assignment'} />

        <Formik
          enableReinitialize={true}
          initialValues={
            isEditing && moduleData
              ? {
                  label: moduleData.label || '',
                  value: moduleData.value || '',
                  url: moduleData.url || '',
                }
              : initialValues
          }
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(formik) => {
            const { values, isSubmitting, setFieldValue, errors, touched } = formik;

            // Store formik bag for external access
            formikBagRef.current = formik;

            return (
              <Form onSubmit={formik.handleSubmit} className='bg-dark'>
                <Card
                  className="border-0 shadow-sm bg-dark"
                  style={{ borderRadius: '15px', overflow: 'hidden' }}
                >
                  <Card.Body className='bg-dark'>
                    <FilterSection title="Module Assignment Details" >
                      <Row className="mb-3">
                        <Col md={6}>
                          <Form.Group controlId="formLabel">
                            <Form.Label style={{ color: THEME.primaryDark }}>
                              Label <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="label"
                              value={values.label}
                              onChange={(e) => {
                                const label = e.target.value;
                                const processed = label.toLowerCase().replace(/\s+/g, '-');
                                setFieldValue('label', label);
                                setFieldValue('value', processed);
                                setFieldValue('url', '/' + processed);
                              }}
                              placeholder="Enter label (e.g., roles)"
                              isInvalid={touched.label && errors.label}
                            />
                            <ErrorMessage name="label" component="div" className="text-danger" />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="formValue">
                            <Form.Label style={{ color: THEME.primaryDark }}>
                              Value <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="value"
                              value={values.value}
                              onChange={(e) =>
                                setFieldValue('value', e.target.value.replace(/\s+/g, '-'))
                              }
                              placeholder="Enter value (e.g., roles)"
                              isInvalid={touched.value && errors.value}
                            />
                            <ErrorMessage name="value" component="div" className="text-danger" />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={6}>
                          <Form.Group controlId="formUrl">
                            <Form.Label style={{ color: THEME.primaryDark }}>
                              URL <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="url"
                              value={values.url}
                              onChange={(e) => {
                                let url = e.target.value;
                                url = url.startsWith('/') ? url.slice(1) : url;
                                setFieldValue('url', '/' + url.replace(/\s+/g, '-'));
                              }}
                              placeholder="Enter URL (e.g., roles)"
                              isInvalid={touched.url && errors.url}
                            />
                            <ErrorMessage name="url" component="div" className="text-danger" />
                          </Form.Group>
                        </Col>
                      </Row>
                    </FilterSection>

                    <div className="d-flex justify-content-end mt-4">
                      <Button
                        variant="secondary"
                        onClick={leave}
                        className="me-2"
                        style={{ borderRadius: '30px', padding: '8px 25px' }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="success"
                        type="submit"
                        disabled={isSubmitting}
                        style={{
                          backgroundColor: THEME.primary,
                          borderColor: THEME.primary,
                          borderRadius: '30px',
                          padding: '8px 30px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        }}
                      >
                        {isSubmitting
                          ? 'Processing...'
                          : isEditing
                          ? 'Update Module Assignment'
                          : 'Add Module Assignment'}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
}

export default AddAssignModules;